import React from 'react';
import { useAppNavigation } from '../hooks/useAppNavigation';

const PrivacyPolicy = () => {
  const navigate = useAppNavigation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
            <button
              onClick={() => navigate.to.HOME()}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Back to App
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="mb-8">
            <p className="text-gray-600">
              <strong>Effective Date:</strong> October 9, 2024<br />
              <strong>Last Updated:</strong> October 9, 2024
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              ClutterLogix respects your privacy. This policy explains how we collect, use, and protect 
              your information when you use our AI-powered home inventory service.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="font-semibold text-gray-900 mb-2">Account Information</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Email address (required)</li>
              <li>Name (optional)</li>
              <li>Profile picture (optional)</li>
              <li>Password (encrypted)</li>
            </ul>

            <h3 className="font-semibold text-gray-900 mb-2">Inventory Data</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Item names, descriptions, and notes</li>
              <li>Photos you upload</li>
              <li>Item details (quantity, value, condition, etc.)</li>
              <li>Box and location information</li>
              <li>AI-generated descriptions and tags</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. AI Processing</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6">
              <p className="font-semibold text-yellow-800">Important:</p>
              <p className="text-yellow-700">We use third-party AI services to analyze your photos:</p>
            </div>

            <h3 className="font-semibold text-gray-900 mb-2">OpenAI (GPT-4 Vision)</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li><strong>What we share:</strong> Your uploaded photos and item descriptions</li>
              <li><strong>Purpose:</strong> Generate automatic descriptions and tags</li>
              <li>
                <strong>Their policy:</strong>{' '}
                <a 
                  href="https://openai.com/privacy/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  https://openai.com/privacy/
                </a>
              </li>
            </ul>

            <h3 className="font-semibold text-gray-900 mb-2">Google Vision API</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li><strong>What we share:</strong> Your uploaded photos</li>
              <li><strong>Purpose:</strong> Object detection and text recognition</li>
              <li>
                <strong>Their policy:</strong>{' '}
                <a 
                  href="https://policies.google.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  https://policies.google.com/privacy
                </a>
              </li>
            </ul>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-6">
              <p className="font-semibold text-blue-800">Important Privacy Protection:</p>
              <p className="text-blue-700">
                When we share your images with AI services (OpenAI, Google Vision), we do not include 
                any personally identifiable information such as your name, email, or account details. 
                The third-party services only receive the image data itself without any direct link 
                to your identity or ClutterLogix account.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Third-Party Services</h2>
            <p className="text-gray-700 mb-4">We use these services to operate ClutterLogix:</p>

            <div className="overflow-x-auto my-6">
              <table className="min-w-full border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Service</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Purpose</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Data Shared</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">OpenAI</td>
                    <td className="border border-gray-300 px-4 py-2">AI processing</td>
                    <td className="border border-gray-300 px-4 py-2">Images, descriptions</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Google Cloud</td>
                    <td className="border border-gray-300 px-4 py-2">Storage & AI</td>
                    <td className="border border-gray-300 px-4 py-2">Images, all data</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Stripe</td>
                    <td className="border border-gray-300 px-4 py-2">Payments</td>
                    <td className="border border-gray-300 px-4 py-2">Billing info</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Heroku</td>
                    <td className="border border-gray-300 px-4 py-2">Backend hosting</td>
                    <td className="border border-gray-300 px-4 py-2">All backend data</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Vercel</td>
                    <td className="border border-gray-300 px-4 py-2">Frontend hosting</td>
                    <td className="border border-gray-300 px-4 py-2">Session data</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Your Privacy Rights</h2>
            
            <h3 className="font-semibold text-gray-900 mb-2">Access Your Data</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>View all your personal information through your account</li>
              <li>Request a copy of your data</li>
            </ul>

            <h3 className="font-semibold text-gray-900 mb-2">Delete Your Data</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Delete individual items or your entire account</li>
              <li>We'll delete your data within 30 days</li>
              <li>Some data may be kept for legal requirements</li>
            </ul>

            <h3 className="font-semibold text-gray-900 mb-2">Control Your Data</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Export your inventory as CSV files</li>
              <li>Opt out of promotional emails</li>
              <li>Control family sharing permissions</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Data Security</h2>
            <p className="text-gray-700 mb-2">We protect your information with:</p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>HTTPS/TLS encryption for data transmission</li>
              <li>Encrypted password storage using industry-standard hashing</li>
              <li>Database encryption at rest (provided by Heroku PostgreSQL)</li>
              <li>Secure authentication systems with JWT tokens</li>
              <li>Limited access controls for our staff</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">11. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              ClutterLogix requires users to be at least 13 years old. By creating an account, you confirm 
              that you meet this age requirement. If we learn that a user is under 13, we'll delete their 
              account and information promptly.
            </p>
            <p className="text-gray-700 mb-4">
              Parents or guardians who believe their child under 13 has created an account should contact 
              us immediately at privacy@clutterlogix.com.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">15. Contact Us</h2>
            <p className="text-gray-700 mb-2">Questions about privacy? Contact us at:</p>
            
            <h3 className="font-semibold text-gray-900 mb-2">Privacy Inquiries:</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Email: privacy@clutterlogix.com</li>
              <li>Response time: We respond within 30 days</li>
            </ul>

            <h3 className="font-semibold text-gray-900 mb-2">General Support:</h3>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
              <li>Email: support@clutterlogix.com</li>
              <li>Website: https://clutterlogix.com</li>
            </ul>

            <div className="mt-12 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
              <p className="text-sm text-gray-700 italic">
                This privacy policy explains how ClutterLogix handles your personal information. 
                By using our service, you agree to the collection and use of information as described in this policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
